import {
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import {
  TargetTierInfo,
  targetTiersApi,
} from '@texas/api/endpoints/targetTiers/targetTiersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SpinnerWithLabel } from '@texas/components/shared/SpinnerWithLabel';
import { useEffect } from 'react';
import { articleManageEvents } from '../events';
import { useSignalSubscriptionEvents } from '@texas/components/shared/hooks/useSignalSubscriptionEvents';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { EditQuantityModal } from './EditQuantityModal';
import { Icons } from '@texas/components/shared/Icons';
import { useTranslation } from 'react-i18next';
import { SimpleNote } from '@texas/components/shared/SimpleNote';
import { angularEvents } from '@bridge/angularEvents';
import { AddMultipleTiersModal } from './AddMultipleTiersModal';

export function ArticleTargetTiers({ articleId }: { articleId: number }) {
  const { t } = useTranslation();
  const { data, loading, refetch } = useApiResource(targetTiersApi.getInfo);

  useEffect(() => {
    refetch(articleId);
  }, [articleId, refetch]);

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      refetch(articleId);
    },
    signalEvents: [
      articleManageEvents.tiersAdded,
      articleManageEvents.tierRemoved,
      articleManageEvents.tierUpdated,
      angularEvents.articleManagementUpdated,
    ],
  });

  return (
    <Box>
      <Text variant="header">{t('tiers.targetTiers.title')}</Text>
      <Text variant="small" pb={2}>
        {t('tiers.targetTiers.desc')}
      </Text>
      {loading && <SpinnerWithLabel label={t('general.loading')} />}
      {data && (
        <Box>
          {data.vdcArticle ? (
            <SimpleNote color="black">
              {t('tiers.targetTiers.notAvailableForVdc')}
            </SimpleNote>
          ) : (
            <TargetTiers info={data} />
          )}
        </Box>
      )}
    </Box>
  );
}

function TargetTiers({ info }: { info: TargetTierInfo }) {
  const { t } = useTranslation();

  const { request: removeTier, loading: removing } = useApiRequest(
    targetTiersApi.removeTier,
  );

  async function performRemove(id: number) {
    const response = await removeTier(id);
    if (response.hasError) {
      return;
    }
    articleManageEvents.tierRemoved.dispatch();
  }

  const {
    isOpen: isEditOpen,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
    value,
  } = useValueDisclosure<{
    id: number;
    quantity: number;
  } | null>();

  const {
    isOpen: isAddOpen,
    onOpen: onOpenAdd,
    onClose: onCloseAdd,
  } = useDisclosure();

  return (
    <Box>
      <Flex direction="column" gap={1} pt={2}>
        {info.targetTiers.map((tier) => {
          return (
            <Flex
              key={tier.id}
              align="center"
              justify="space-between"
              px={3}
              py={2}
              borderRadius="md"
              bg="texas.bg.800"
              _light={{ bg: 'gray.50' }}
              cursor="pointer"
              onClick={() =>
                onOpenEdit({ id: tier.id, quantity: tier.quantity })
              }
              _hover={{ opacity: 0.8 }}
            >
              <Text fontFamily="barlow" fontWeight="bold" fontSize="sm">
                {t('general.quantityCount', { count: tier.quantity })}
              </Text>
              <Flex gap={1} onClick={(e) => e.stopPropagation()}>
                <IconButton
                  icon={<Icons.Pencil boxSize={3} />}
                  aria-label={t('general.edit')}
                  size="xs"
                  variant="no-bg"
                  onClick={() =>
                    onOpenEdit({ id: tier.id, quantity: tier.quantity })
                  }
                />{' '}
                <Tooltip label={t('general.clickToRemove')}>
                  <IconButton
                    icon={<Icons.Close boxSize={3} />}
                    aria-label={t('general.clickToRemove')}
                    size="xs"
                    colorScheme="red"
                    variant="ghost"
                    isLoading={removing}
                    onClick={async () => {
                      await performRemove(tier.id);
                    }}
                  />
                </Tooltip>
              </Flex>
            </Flex>
          );
        })}
      </Flex>
      <Button
        mt={3}
        color="articleManagement.text"
        borderColor="articleManagement.inputBorder"
        leftIcon={<Icons.Plus />}
        variant="texas-outline"
        onClick={onOpenAdd}
        _hover={{ borderColor: 'articleManagement.inputBorderHover' }}
      >
        {t('tiers.add')}
      </Button>
      {value && (
        <EditQuantityModal
          isOpen={isEditOpen}
          onClose={onCloseEdit}
          tierId={value.id}
          quantity={value.quantity}
        />
      )}
      <AddMultipleTiersModal
        isOpen={isAddOpen}
        onClose={onCloseAdd}
        articleId={info.articleId}
        existingQuantities={info.targetTiers.map((t) => t.quantity)}
      />
    </Box>
  );
}
